/*
  ЦИФРОВОЙ ДИАК ЗФ — BACKLOG SUBTASKS
  Лёгкий чек-лист подзадач внутри карточки канбана. UX вдохновлён Asana
  (заголовок + чекбокс + опциональный дедлайн), Linear (drag-reorder и
  компактные строки) и Trello (быстрый ввод через Enter, прогресс-бар).

  Поведение:
    • Клик по заголовку → inline-редактирование; Enter / blur — сохранить, Esc — отменить.
    • Клик по чекбоксу — переключить готовность (с completedAt).
    • Перетаскивание ручкой слева — переупорядочить (dnd-kit).
    • Маленькая кнопка-календарь — добавить/изменить/убрать дедлайн.
    • Кнопка-корзина (на hover) — удалить подзадачу с подтверждением.
    • Прогресс-бар «N/M · X%» сверху.
    • Если выполненных ≥ 3 — появляется тумблер «Скрыть выполненные».
*/

import React, { useEffect, useRef, useState } from "react";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  CalendarDays,
  Check,
  Download,
  File as FileIcon,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileType,
  GripVertical,
  ListChecks,
  Loader2,
  Mail,
  Paperclip,
  Trash2,
  Upload,
  UserPlus,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import type { BacklogSubtask, BacklogSubtaskStatus, SubtaskAttachment } from "@/store/projectStore";
import {
  uploadBacklogAttachment,
  deleteProjectFileOnServer,
  projectFileUrl,
} from "@/lib/api";
import { SUBTASK_STATUS_META, SUBTASK_STATUS_ORDER } from "@/lib/subtaskStatus";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  colorForAssignee,
  initialsFor,
  normalizeAssigneeKey,
  preferredAssigneeLabel,
} from "@/lib/assigneeDisplay";
import { useNotifications } from "@/contexts/NotificationsContext";
import { useAssigneeSuggestions } from "@/hooks/useAssigneeSuggestions";

interface BacklogSubtasksProps {
  items: BacklogSubtask[];
  onChange: (next: BacklogSubtask[]) => void;
  readOnly?: boolean;
  /** Используется для аплоада/удаления вложений подзадач через тот же API карточек. */
  projectId?: string;
}

const MAX_TITLE = 500;

function newId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `s-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

/** «3 апр», «вчера», «сегодня», «завтра», «через 5 дней» */
function describeDue(iso: string | null | undefined): {
  short: string;
  tone: "ok" | "soon" | "overdue";
  full: string;
} | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const target = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const t0 = target.getTime();
  const n0 = today.getTime();
  const diffDays = Math.round((t0 - n0) / 86_400_000);
  const fmt = target.toLocaleDateString("ru-RU", { day: "2-digit", month: "short" });
  let short: string;
  if (diffDays === 0) short = "сегодня";
  else if (diffDays === 1) short = "завтра";
  else if (diffDays === -1) short = "вчера";
  else short = fmt;
  let tone: "ok" | "soon" | "overdue";
  if (diffDays < 0) tone = "overdue";
  else if (diffDays <= 2) tone = "soon";
  else tone = "ok";
  const full = `Дедлайн: ${target.toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  })}${diffDays >= 0 ? ` · через ${diffDays} ${plural(diffDays, ["день", "дня", "дней"])}` : ` · ${Math.abs(
    diffDays,
  )} ${plural(Math.abs(diffDays), ["день", "дня", "дней"])} назад`}`;
  return { short, tone, full };
}

function plural(n: number, words: [string, string, string]): string {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return words[2];
  if (b > 1 && b < 5) return words[1];
  if (b === 1) return words[0];
  return words[2];
}

export function BacklogSubtasks({ items, onChange, readOnly = false, projectId }: BacklogSubtasksProps) {
  const list = Array.isArray(items) ? items : [];
  const sorted = [...list].sort((a, b) => a.order - b.order);
  const total = sorted.length;
  const doneCount = sorted.filter((s) => s.status === "done").length;
  const pct = total === 0 ? 0 : Math.round((doneCount / total) * 100);

  const [hideDone, setHideDone] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  const visible = hideDone ? sorted.filter((s) => s.status !== "done") : sorted;

  const reorder = (from: string, to: string) => {
    const oldIdx = sorted.findIndex((s) => s.id === from);
    const newIdx = sorted.findIndex((s) => s.id === to);
    if (oldIdx < 0 || newIdx < 0 || oldIdx === newIdx) return;
    const next = arrayMove(sorted, oldIdx, newIdx).map((s, i) => ({ ...s, order: i }));
    onChange(next);
  };

  const update = (id: string, patch: Partial<BacklogSubtask>) => {
    const next = sorted.map((s) => (s.id === id ? { ...s, ...patch } : s));
    onChange(next);
  };

  const setStatus = (id: string, status: BacklogSubtaskStatus) => {
    const target = sorted.find((s) => s.id === id);
    if (!target) return;
    const wasDone = target.status === "done";
    const isDone = status === "done";
    // При откате из «Сделано» все final-вложения становятся промежуточными результатами.
    // При повторном переходе в «Сделано» уже промежуточные не возвращаются в final
    // автоматически — final-метку получают только файлы, загруженные именно сейчас.
    const nextAttachments =
      wasDone && !isDone && target.attachments && target.attachments.length > 0
        ? target.attachments.map((a) => (a.kind === "final" ? { ...a, kind: "intermediate" as const } : a))
        : target.attachments;
    update(id, {
      status,
      // Дублёр для backward-compat: старые версии клиента/скрипты читают `done`.
      done: isDone,
      completedAt: isDone
        ? wasDone && target.completedAt
          ? target.completedAt
          : new Date().toISOString()
        : null,
      attachments: nextAttachments,
    });
  };

  const remove = (id: string) => {
    const target = sorted.find((s) => s.id === id);
    if (!target) return;
    if (typeof window !== "undefined") {
      const ok = window.confirm(
        `Удалить подзадачу «${target.title || "без названия"}»? Действие необратимо.`,
      );
      if (!ok) return;
    }
    const next = sorted.filter((s) => s.id !== id).map((s, i) => ({ ...s, order: i }));
    onChange(next);
  };

  const add = () => {
    const t = draft.trim();
    if (!t) return;
    const sub: BacklogSubtask = {
      id: newId(),
      title: t.slice(0, MAX_TITLE),
      status: "todo",
      done: false,
      order: sorted.length,
      createdAt: new Date().toISOString(),
    };
    onChange([...sorted, sub]);
    setDraft("");
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (e: DragEndEvent) => {
    const activeId = String(e.active.id);
    const overId = e.over ? String(e.over.id) : null;
    if (!overId || activeId === overId) return;
    reorder(activeId, overId);
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2 text-[11px] text-muted-foreground">
          <ListChecks className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
          <span className="font-semibold uppercase tracking-wide">Подзадачи</span>
          {total > 0 ? (
            <>
              <span className="rounded-full bg-muted/60 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-foreground/80">
                {doneCount}/{total}
              </span>
              <span className="tabular-nums text-muted-foreground/80">{pct}%</span>
            </>
          ) : null}
        </div>
        {doneCount >= 3 && total > 0 ? (
          <button
            type="button"
            onClick={() => setHideDone((v) => !v)}
            className="rounded-md border border-border/70 bg-background px-2 py-0.5 text-[10.5px] font-medium text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground"
            title={hideDone ? "Показать выполненные" : "Скрыть выполненные"}
          >
            {hideDone ? `Показать ${doneCount} выполненных` : `Скрыть выполненные (${doneCount})`}
          </button>
        ) : null}
      </div>

      {/* Progress bar */}
      {total > 0 ? (
        <div
          className="mb-2 h-1 w-full overflow-hidden rounded-full bg-muted/60"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Прогресс подзадач: ${doneCount} из ${total} (${pct}%)`}
        >
          <div
            className={cn(
              "h-full rounded-full transition-[width] duration-300 ease-out",
              pct === 100 ? "bg-emerald-500" : "bg-[var(--nn-blue)]",
            )}
            style={{ width: `${pct}%` }}
          />
        </div>
      ) : null}

      {/* List */}
      {total === 0 ? (
        <p className="rounded-md border border-dashed border-border/70 bg-background/40 px-3 py-2.5 text-center text-[12px] italic text-muted-foreground">
          {readOnly
            ? "Подзадач нет."
            : "Разбейте задачу на шаги — добавьте первую подзадачу ниже."}
        </p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={visible.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            <ul className="space-y-1">
              {visible.map((s) => (
                <SubtaskRow
                  key={s.id}
                  subtask={s}
                  readOnly={readOnly}
                  projectId={projectId}
                  onSetStatus={(status) => setStatus(s.id, status)}
                  onChange={(patch) => update(s.id, patch)}
                  onRemove={() => remove(s.id)}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      {/* Add input */}
      {!readOnly ? (
        <div className="mt-2 flex items-center gap-1.5">
          <span
            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded border border-dashed border-border/80 text-muted-foreground/60"
            aria-hidden
          >
            +
          </span>
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, MAX_TITLE))}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                add();
              } else if (e.key === "Escape") {
                setDraft("");
                inputRef.current?.blur();
              }
            }}
            placeholder="Добавить подзадачу… (Enter — добавить)"
            className="min-h-[1.875rem] flex-1 rounded-md border border-border/70 bg-background px-2 py-1 text-[13px] text-foreground placeholder:text-muted-foreground/70 focus:border-[var(--nn-blue)] focus:outline-none focus:ring-2 focus:ring-[var(--nn-blue)]/20"
          />
          {draft.trim() ? (
            <button
              type="button"
              onClick={add}
              className="inline-flex items-center justify-center rounded-md bg-[var(--nn-blue)] px-2.5 py-1 text-[12px] font-semibold text-white shadow-sm hover:opacity-90"
              title="Добавить (Enter)"
            >
              Добавить
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/* ───────────────────────────── SubtaskRow ────────────────────────────── */

interface SubtaskRowProps {
  subtask: BacklogSubtask;
  readOnly: boolean;
  projectId?: string;
  onSetStatus: (status: BacklogSubtaskStatus) => void;
  onChange: (patch: Partial<BacklogSubtask>) => void;
  onRemove: () => void;
}

function SubtaskRow({ subtask, readOnly, projectId, onSetStatus, onChange, onRemove }: SubtaskRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: subtask.id,
    disabled: readOnly,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
  };

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(subtask.title);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dateInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setDraft(subtask.title);
  }, [subtask.title]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const commitTitle = () => {
    const next = draft.trim();
    if (next && next !== subtask.title) onChange({ title: next.slice(0, MAX_TITLE) });
    setEditing(false);
  };

  const cancelTitle = () => {
    setDraft(subtask.title);
    setEditing(false);
  };

  const dueRaw = describeDue(subtask.dueDate);
  const statusMeta = SUBTASK_STATUS_META[subtask.status];
  const StatusIcon = statusMeta.icon;
  const isDone = subtask.status === "done";
  // Для выполненной подзадачи дата перестаёт быть «горящей» — переключаем
  // в нейтральный тон, чтобы не путать красным.
  const due = dueRaw ? { ...dueRaw, tone: isDone ? ("ok" as const) : dueRaw.tone } : null;

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        "group/subtask flex flex-col rounded-md border border-transparent px-1.5 py-1 transition-colors",
        isDragging && "z-10 border-border bg-background shadow-md",
        !isDragging && "hover:border-border/60 hover:bg-muted/30",
      )}
    >
      <div className="flex w-full items-center gap-1.5">
      {/* Drag handle */}
      {!readOnly ? (
        <button
          type="button"
          className="flex h-5 w-3 shrink-0 cursor-grab touch-none items-center justify-center rounded text-muted-foreground/40 opacity-0 transition-opacity hover:text-foreground group-hover/subtask:opacity-100 active:cursor-grabbing"
          aria-label="Перетащить подзадачу"
          title="Перетащить"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-3 w-3" strokeWidth={2} />
        </button>
      ) : (
        <span className="h-5 w-3 shrink-0" aria-hidden />
      )}

      {/* Status indicator with dropdown picker */}
      {readOnly ? (
        <Tooltip delayDuration={250}>
          <TooltipTrigger asChild>
            <span
              className={cn(
                "flex h-5 w-5 shrink-0 items-center justify-center rounded",
                statusMeta.textClass,
              )}
              aria-label={`Статус: ${statusMeta.label}`}
            >
              <StatusIcon
                className={cn(
                  "h-4 w-4",
                  subtask.status === "in_progress" && "animate-spin-slow",
                )}
                strokeWidth={2}
              />
            </span>
          </TooltipTrigger>
          <TooltipContent className="text-xs">
            <span className="font-semibold">{statusMeta.label}</span>
            {statusMeta.hint ? (
              <span className="block text-muted-foreground">{statusMeta.hint}</span>
            ) : null}
          </TooltipContent>
        </Tooltip>
      ) : (
        <DropdownMenu>
          <Tooltip delayDuration={250}>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    "flex h-5 w-5 shrink-0 items-center justify-center rounded transition-colors hover:bg-muted",
                    statusMeta.textClass,
                  )}
                  aria-label={`Статус: ${statusMeta.label}. Изменить статус.`}
                >
                  <StatusIcon
                    className={cn(
                      "h-4 w-4",
                      subtask.status === "in_progress" && "animate-spin-slow",
                    )}
                    strokeWidth={2}
                  />
                </button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent className="text-xs">
              <span className="font-semibold">{statusMeta.label}</span>
              {statusMeta.hint ? (
                <span className="block text-muted-foreground">{statusMeta.hint}</span>
              ) : null}
              <span className="block pt-0.5 text-muted-foreground/80">Кликните, чтобы изменить</span>
            </TooltipContent>
          </Tooltip>
          <DropdownMenuContent align="start" className="min-w-[10rem]">
            {SUBTASK_STATUS_ORDER.map((s, idx) => {
              const m = SUBTASK_STATUS_META[s];
              const Icon = m.icon;
              const active = subtask.status === s;
              return (
                <React.Fragment key={s}>
                  {idx === SUBTASK_STATUS_ORDER.length - 1 ? (
                    <DropdownMenuSeparator />
                  ) : null}
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      onSetStatus(s);
                    }}
                    className="cursor-pointer items-center gap-2 py-1.5"
                  >
                    <Icon
                      className={cn(
                        "h-3.5 w-3.5 shrink-0",
                        m.textClass,
                        s === "in_progress" && "animate-spin-slow",
                      )}
                      strokeWidth={2}
                    />
                    <span className="min-w-0 flex-1 text-[13px]">{m.label}</span>
                    {active ? (
                      <Check className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={2.2} />
                    ) : null}
                  </DropdownMenuItem>
                </React.Fragment>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Title */}
      {editing && !readOnly ? (
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, MAX_TITLE))}
          onBlur={commitTitle}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitTitle();
            } else if (e.key === "Escape") {
              e.preventDefault();
              cancelTitle();
            }
          }}
          className="min-w-0 flex-1 rounded border border-[var(--nn-blue)] bg-[var(--nn-blue-pale)]/40 px-1.5 py-0.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-[var(--nn-blue)]/30"
        />
      ) : (
        <button
          type="button"
          onClick={() => !readOnly && setEditing(true)}
          disabled={readOnly}
          className={cn(
            "min-w-0 flex-1 whitespace-normal break-words rounded px-1 py-0.5 text-left text-[13px] leading-snug transition-colors",
            isDone && "text-muted-foreground line-through decoration-muted-foreground/60",
            !readOnly && "cursor-text hover:bg-background",
          )}
          title={readOnly ? subtask.title : "Изменить название"}
        >
          {subtask.title || (
            <span className="italic text-muted-foreground/70">Без названия</span>
          )}
        </button>
      )}

      {/* Due date chip / placeholder */}
      <span className="inline-flex items-center">
        {due ? (
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              <span
                className={cn(
                  "inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold tabular-nums",
                  due.tone === "overdue" &&
                    "border-red-300/80 bg-red-50 text-red-700 dark:border-red-800/60 dark:bg-red-950/40 dark:text-red-300",
                  due.tone === "soon" &&
                    "border-amber-300/80 bg-amber-50 text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-300",
                  due.tone === "ok" &&
                    "border-border/70 bg-muted/40 text-muted-foreground",
                )}
                aria-label={due.full}
              >
                <CalendarDays className="h-2.5 w-2.5" strokeWidth={2.5} />
                {readOnly ? (
                  <span>{due.short}</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      const el = dateInputRef.current;
                      if (!el) return;
                      if (typeof el.showPicker === "function") el.showPicker();
                      else el.focus();
                    }}
                    className="cursor-pointer rounded px-0.5 hover:underline"
                    title="Изменить дедлайн"
                  >
                    {due.short}
                  </button>
                )}
                {!readOnly ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange({ dueDate: null });
                    }}
                    className="-mr-0.5 ml-0.5 inline-flex h-3 w-3 items-center justify-center rounded-full opacity-70 hover:opacity-100"
                    aria-label="Убрать дедлайн"
                    title="Убрать дедлайн"
                  >
                    <X className="h-2.5 w-2.5" strokeWidth={2.5} />
                  </button>
                ) : null}
              </span>
            </TooltipTrigger>
            <TooltipContent className="text-xs">{due.full}</TooltipContent>
          </Tooltip>
        ) : !readOnly ? (
          <span className="relative inline-flex">
            <button
              type="button"
              onClick={() => {
                const el = dateInputRef.current;
                if (!el) return;
                if (typeof el.showPicker === "function") el.showPicker();
                else el.focus();
              }}
              className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground/50 opacity-0 transition-all hover:bg-muted hover:text-foreground group-hover/subtask:opacity-100 focus-visible:opacity-100"
              aria-label="Установить дедлайн"
              title="Установить дедлайн"
            >
              <CalendarDays className="h-3.5 w-3.5" strokeWidth={2} />
            </button>
          </span>
        ) : null}
        {/* Скрытый натив-инпут для showPicker(). Вне видимой области — чтобы не перехватывал клики. */}
        {!readOnly ? (
          <input
            ref={dateInputRef}
            type="date"
            value={subtask.dueDate ?? ""}
            onChange={(e) => onChange({ dueDate: e.target.value || null })}
            className="sr-only"
            aria-hidden
            tabIndex={-1}
          />
        ) : null}
      </span>

      {/* Assignee avatar */}
      <SubtaskAssigneeAvatar
        value={subtask.assignee ?? ""}
        onChange={(v) => onChange({ assignee: v ? v : undefined })}
        readOnly={readOnly}
      />

      {/* Trash */}
      {!readOnly ? (
        <button
          type="button"
          onClick={onRemove}
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground/40 opacity-0 transition-all hover:bg-red-50 hover:text-red-600 group-hover/subtask:opacity-100 focus-visible:opacity-100 dark:hover:bg-red-950/40 dark:hover:text-red-300"
          aria-label="Удалить подзадачу"
          title="Удалить"
        >
          <Trash2 className="h-3 w-3" strokeWidth={2} />
        </button>
      ) : null}
      </div>
      {projectId ? (
        <SubtaskAttachmentsBlock
          projectId={projectId}
          subtask={subtask}
          readOnly={readOnly}
          onChange={onChange}
        />
      ) : null}
    </li>
  );
}

/* ────────────────────── SubtaskAssigneeAvatar ─────────────────────── */
/*
  Компактный пикер исполнителя для строки подзадачи. UX: только круглый
  мини-аватар (или dashed-плюс если не назначен), клик открывает popover
  с тем же поиском, что и пикер на родительской карточке.
*/

interface SubtaskAssigneeAvatarProps {
  value: string;
  onChange: (next: string) => void;
  readOnly: boolean;
}

function SubtaskAssigneeAvatar({ value, onChange, readOnly }: SubtaskAssigneeAvatarProps) {
  const { assigneeMatches } = useNotifications();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const items = useAssigneeSuggestions(open, query);

  const raw = (value || "").trim();
  const match = raw ? assigneeMatches.get(normalizeAssigneeKey(raw)) ?? null : null;
  const label = raw ? preferredAssigneeLabel(raw, match) : "";
  const initials = initialsFor(match?.displayName || match?.login || raw);
  const bg = match ? colorForAssignee(normalizeAssigneeKey(match.login)) : "";

  const assign = (next: string) => {
    const t = next.trim();
    onChange(t);
    setQuery("");
    setOpen(false);
  };

  const tooltip = match
    ? match.isRegistered
      ? `${match.login} — зарегистрирован, уведомление придёт`
      : `${match.login} — приглашён, но ещё не зарегистрировался`
    : raw
      ? "Не найден среди пользователей — уведомление не будет отправлено"
      : "";

  const trigger = raw ? (
    <button
      type="button"
      className={cn(
        "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-semibold transition-transform",
        match ? "text-white" : "border border-dashed border-border bg-muted/40 text-muted-foreground",
        readOnly ? "cursor-default" : "cursor-pointer hover:scale-110",
      )}
      style={match ? { backgroundColor: bg } : undefined}
      disabled={readOnly}
      aria-label={`Исполнитель: ${label}. Кликните, чтобы изменить.`}
      title={tooltip ? `${label}\n${tooltip}` : label}
    >
      {initials || "?"}
    </button>
  ) : (
    <button
      type="button"
      className={cn(
        "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-dashed border-border/80 text-muted-foreground/60 transition-all",
        readOnly
          ? "cursor-default opacity-0"
          : "cursor-pointer opacity-0 group-hover/subtask:opacity-100 hover:border-[var(--nn-blue)] hover:bg-[var(--nn-blue-pale)]/40 hover:text-[var(--nn-blue)] focus-visible:opacity-100",
      )}
      disabled={readOnly}
      aria-label="Назначить исполнителя на подзадачу"
      title="Назначить исполнителя"
    >
      <UserPlus className="h-3 w-3" strokeWidth={2.2} />
    </button>
  );

  if (readOnly) return trigger;

  const selectedKey = raw ? normalizeAssigneeKey(raw) : "";
  const showAddRawButton =
    query.trim().length > 0 &&
    normalizeAssigneeKey(query) !== selectedKey &&
    !items.some(
      (it) =>
        normalizeAssigneeKey(it.login) === normalizeAssigneeKey(query) ||
        (it.displayName && normalizeAssigneeKey(it.displayName) === normalizeAssigneeKey(query)),
    );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={6}
        className="z-[100] w-[min(22rem,calc(100vw-1.5rem))] p-0 shadow-lg"
      >
        <Command shouldFilter={false} className="h-auto max-h-[min(20rem,calc(100dvh-9rem))] min-h-0 flex-col rounded-md">
          <CommandInput
            placeholder="Найти по ФИО или e-mail…"
            value={query}
            onValueChange={setQuery}
            onKeyDown={(e) => {
              if (e.key === "Enter" && query.trim()) {
                assign(query);
                e.preventDefault();
              }
            }}
          />
          <CommandList
            className="max-h-[min(16rem,calc(100dvh-11rem))] min-h-0 flex-1 overflow-y-auto overscroll-y-contain"
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            {items.length === 0 && !showAddRawButton ? (
              <CommandEmpty>Никого не найдено</CommandEmpty>
            ) : null}
            {raw ? (
              <CommandGroup heading="Текущий">
                <CommandItem
                  value={`__clear__ ${raw}`}
                  onSelect={() => assign("")}
                  className="cursor-pointer items-center gap-2 py-2"
                >
                  <span
                    aria-hidden
                    className="inline-flex size-6 shrink-0 items-center justify-center rounded-full border border-dashed border-border bg-muted/40 text-[10px] text-muted-foreground"
                  >
                    <X className="h-3 w-3" strokeWidth={2.2} />
                  </span>
                  <span className="min-w-0 flex-1 leading-tight">
                    <span className="block truncate text-sm">Не назначен</span>
                    <span className="block truncate text-[11px] text-muted-foreground">
                      Снять текущего: {label}
                    </span>
                  </span>
                </CommandItem>
              </CommandGroup>
            ) : null}
            {items.length > 0 ? (
              <CommandGroup heading="Пользователи и приглашения">
                {items.map((it) => {
                  const key = normalizeAssigneeKey(it.login);
                  const itemLabel = it.displayName?.trim() || it.login;
                  const selected = key === selectedKey;
                  return (
                    <CommandItem
                      key={key}
                      value={`${it.login} ${it.displayName ?? ""}`}
                      onSelect={() => assign(it.displayName?.trim() || it.login)}
                      className="cursor-pointer items-center gap-2 py-2"
                    >
                      <span
                        aria-hidden
                        className="inline-flex size-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white"
                        style={{ backgroundColor: colorForAssignee(key) }}
                      >
                        {initialsFor(itemLabel)}
                      </span>
                      <span className="min-w-0 flex-1 leading-tight">
                        <span className="block truncate text-sm">{itemLabel}</span>
                        <span className="block truncate text-[11px] text-muted-foreground">
                          {it.login}
                          {" · "}
                          {it.isRegistered ? (
                            <span className="text-emerald-600">зарегистрирован</span>
                          ) : (
                            <span>приглашён</span>
                          )}
                        </span>
                      </span>
                      {selected ? (
                        <span className="ml-auto text-[10px] uppercase tracking-wide text-muted-foreground">
                          выбран
                        </span>
                      ) : null}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ) : null}
            {showAddRawButton ? (
              <CommandGroup heading="Произвольное значение">
                <CommandItem
                  value={`__free__ ${query}`}
                  onSelect={() => assign(query)}
                  className="cursor-pointer items-center gap-2 py-2"
                >
                  <span
                    aria-hidden
                    className="inline-flex size-6 shrink-0 items-center justify-center rounded-full border border-dashed border-border bg-muted/40 text-[10px] text-muted-foreground"
                  >
                    <UserPlus className="h-3 w-3" strokeWidth={2.2} />
                  </span>
                  <span className="min-w-0 flex-1 leading-tight">
                    <span className="block truncate text-sm">
                      Использовать «{query.trim()}»
                    </span>
                    <span className="block truncate text-[11px] text-muted-foreground">
                      Запись не привязана к пользователю — уведомления не будет
                    </span>
                  </span>
                </CommandItem>
              </CommandGroup>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/* ───────────────────── SubtaskAttachmentsBlock ──────────────────────── */
/*
  Блок вложений подзадачи. Файлы хранятся через тот же API, что и вложения
  карточки (`/api/workspace/project-files/upload`). Загружать новые можно
  только когда подзадача в статусе «Сделано» — это якорь UX «вот доказательство
  выполнения». Уже прикреплённые файлы остаются доступны при любом статусе:
  при возврате подзадачи из «Сделано» все `final`-вложения помечаются как
  `intermediate` (промежуточный результат) — логика в setStatus родителя.
*/

function fmtBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "0 Б";
  const units = ["Б", "КБ", "МБ", "ГБ"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  const rounded = v >= 10 ? Math.round(v) : Math.round(v * 10) / 10;
  return `${rounded} ${units[i]}`;
}

function iconForAttachment(
  mime: string,
  name?: string,
): React.ComponentType<{ className?: string; strokeWidth?: number }> {
  if (mime.startsWith("image/")) return FileImage;
  if (mime === "application/pdf") return FileText;
  if (
    mime === "application/vnd.ms-excel" ||
    mime === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  )
    return FileSpreadsheet;
  if (
    mime === "application/msword" ||
    mime === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  )
    return FileType;
  if (mime === "text/plain") return FileText;
  if (
    mime === "message/rfc822" ||
    mime === "application/eml" ||
    mime === "application/x-eml" ||
    (!!name && /\.eml$/i.test(name))
  ) {
    return Mail;
  }
  return FileIcon;
}

interface SubtaskAttachmentsBlockProps {
  projectId: string;
  subtask: BacklogSubtask;
  readOnly: boolean;
  onChange: (patch: Partial<BacklogSubtask>) => void;
}

function SubtaskAttachmentsBlock({
  projectId,
  subtask,
  readOnly,
  onChange,
}: SubtaskAttachmentsBlockProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploadingNames, setUploadingNames] = useState<string[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const attachments = subtask.attachments ?? [];
  const isDone = subtask.status === "done";
  const canUpload = !readOnly && isDone;

  if (attachments.length === 0 && uploadingNames.length === 0 && !canUpload) return null;

  const uploadFiles = async (files: File[]) => {
    if (!canUpload || files.length === 0) return;
    const names = files.map((f) => f.name || "file");
    setUploadingNames((prev) => [...prev, ...names]);
    const added: SubtaskAttachment[] = [];
    for (const file of files) {
      try {
        const att = await uploadBacklogAttachment(projectId, file);
        added.push({ ...att, kind: "final" });
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Ошибка загрузки";
        toast.error(`Не удалось загрузить «${file.name}»: ${msg}`);
      } finally {
        setUploadingNames((prev) => {
          const idx = prev.indexOf(file.name || "file");
          if (idx < 0) return prev;
          const next = prev.slice();
          next.splice(idx, 1);
          return next;
        });
      }
    }
    if (added.length > 0) {
      onChange({ attachments: [...attachments, ...added] });
    }
  };

  const onPickClick = () => {
    if (!canUpload) return;
    inputRef.current?.click();
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files;
    if (!list || list.length === 0) return;
    void uploadFiles(Array.from(list));
    e.target.value = "";
  };

  const removeAtt = async (att: SubtaskAttachment) => {
    if (readOnly) return;
    if (typeof window !== "undefined") {
      const ok = window.confirm(`Удалить файл «${att.name}»? Действие необратимо.`);
      if (!ok) return;
    }
    setBusyId(att.id);
    try {
      await deleteProjectFileOnServer(projectId, att.id);
      onChange({ attachments: attachments.filter((a) => a.id !== att.id) });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Ошибка удаления";
      toast.error(`Не удалось удалить файл: ${msg}`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="ml-5 mt-1.5 space-y-1">
      {attachments.length > 0 ? (
        <ul className="space-y-0.5">
          {attachments.map((att) => {
            const Icon = iconForAttachment(att.mime, att.name);
            const isFinal = att.kind === "final";
            return (
              <li
                key={att.id}
                className={cn(
                  "group flex items-center gap-1.5 rounded border border-border/60 bg-background/60 px-1.5 py-1 text-[11.5px]",
                  !isFinal && "border-dashed border-amber-300/70 bg-amber-50/40 dark:border-amber-500/40 dark:bg-amber-950/20",
                )}
              >
                <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={2} />
                <a
                  href={projectFileUrl(projectId, att.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="min-w-0 flex-1 truncate text-foreground/85 hover:text-[var(--nn-blue)] hover:underline"
                  title={att.name}
                >
                  {att.name}
                </a>
                <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground/80">
                  {fmtBytes(att.size)}
                </span>
                {!isFinal ? (
                  <span
                    className="shrink-0 rounded bg-amber-100 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                    title="Загружено как финальный результат, но подзадачу вернули в работу — теперь это промежуточный результат"
                  >
                    промежуточный
                  </span>
                ) : null}
                <a
                  href={projectFileUrl(projectId, att.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground/60 opacity-0 transition-all hover:bg-[var(--nn-blue-pale)]/60 hover:text-[var(--nn-blue)] group-hover:opacity-100"
                  aria-label={`Скачать ${att.name}`}
                  title="Скачать"
                >
                  <Download className="h-3 w-3" strokeWidth={2} />
                </a>
                {!readOnly ? (
                  <button
                    type="button"
                    onClick={() => void removeAtt(att)}
                    disabled={busyId === att.id}
                    className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground/40 opacity-0 transition-all hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 disabled:opacity-40 dark:hover:bg-red-950/40 dark:hover:text-red-300"
                    aria-label={`Удалить ${att.name}`}
                    title="Удалить"
                  >
                    {busyId === att.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Trash2 className="h-3 w-3" strokeWidth={2} />
                    )}
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
      {uploadingNames.length > 0 ? (
        <ul className="space-y-0.5">
          {uploadingNames.map((n, i) => (
            <li
              key={`${n}-${i}`}
              className="flex items-center gap-1.5 rounded border border-dashed border-border/60 bg-muted/30 px-1.5 py-1 text-[11.5px] text-muted-foreground"
            >
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
              <span className="min-w-0 flex-1 truncate">{n}</span>
              <span className="shrink-0 text-[10px] uppercase tracking-wide">загрузка…</span>
            </li>
          ))}
        </ul>
      ) : null}
      {canUpload ? (
        <>
          <input
            ref={inputRef}
            type="file"
            multiple
            className="sr-only"
            onChange={onInputChange}
            tabIndex={-1}
            aria-hidden
          />
          <button
            type="button"
            onClick={onPickClick}
            className="inline-flex items-center gap-1 rounded border border-dashed border-border/70 bg-background/40 px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:border-[var(--nn-blue)]/60 hover:bg-[var(--nn-blue-pale)]/40 hover:text-[var(--nn-blue)]"
          >
            <Paperclip className="h-3 w-3" strokeWidth={2} />
            Прикрепить файл
            <Upload className="h-3 w-3 opacity-70" strokeWidth={2} />
          </button>
        </>
      ) : attachments.length === 0 ? null : !isDone ? (
        <p className="text-[10.5px] italic text-muted-foreground/70">
          Загрузка новых файлов доступна только в статусе «Сделано».
        </p>
      ) : null}
    </div>
  );
}
